<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
				<div class="col-md-12">
          <h3 v-if="resource.name">{{ resource.name }}</h3>
          <h3 v-else>{{ _('New Resource') }}</h3>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <editor-tabs id="tabNavi" :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-lg-6 col-sm-8 leftcol">
                <h3>{{ _('General') }}</h3>
                <div class="form-group languageversioned">
                  <label for="name">{{ _('Name') }}</label>
                  <input id="name" class="form-control" name="name" type="text" v-model="resource.name" />
                </div>
                <div class="form-group">
                  <label for="type">{{ _('Type') }}</label>
                  <select class="form-control" id="resourceType" name="displayedtype" v-model="resource.displayedtype">
                    <option v-if="resource.displayedtype === 'buyable'" value="buyable">{{ _('Stock resource') }}</option>
                    <option v-else-if="resource.displayedtype === 'openinghours'" value="openinghours">{{ _('Opening hours') }}</option>
                    <template v-else>
                      <option v-for="resourceType in limitedReservableResourceTypes" :value="resourceType.value">{{ resourceType.name }}</option>
                    </template>
                  </select>
                </div>
                <div class="form-group">
                  <label for="name">{{ _('Identifier') }}</label>
                  <input id="custom_id" class="form-control" name="custom_id" type="text" v-model="resource.custom_id" />
                </div>
                <template v-if="resource.displayedtype !== 'openinghours'">
                  <div v-if="resource.displayedtype === 'buyable'">
                    <div class="form-group">
                      <label for="count">{{ _('Stock balance') }}</label>
                      <div class="row">
                        <div class="col-md-9">
                          <input id="count" class="form-control" name="count" type="number" data-android-inputmode="decimal" v-model="resource.capacity" />
                        </div>
                        <div class="col-md-3">
                          <select class="form-control" name="unit" v-model="resource.unit" :disabled="isUnitLocked">
                          <option value="pcs">{{ _('pcs') }}</option>
                          <option value="m3">m&#179;</option>
                          <option value="l">l</option>
                          <option value="dl">dl</option>
                          <option value="cl">cl</option>
                          <option value="ml">ml</option>
                          <option value="kg">kg</option>
                          <option value="g">g</option>
                          <option value="km">km</option>
                          <option value="m">m</option>
                          <option value="m2">m&#178;</option>
                        </select>
                        </div>
                      </div>
                    </div>
                    <p v-if="resource.basketitemcount">{{ _('Stock balance currently in baskets:') }} {{ resource.basketitemcount }}</p>
                  </div>
                  <div v-else>
                    <div class="form-group">
                      <label for="mincount">{{ _('Minimum capacity of the resource') }}</label>
                      <input id="minCount" class="form-control" name="mincount" type="number" data-android-inputmode="decimal" v-model="resource.mincount" />
                    </div>
                    <p>{{ _('If you want to set a minimum number of participants for the reservation, you can enter the value in the field "Minimum capacity of the resource".') }}</p>
                    <div class="form-group">
                      <label for="count">{{ _('Maximum capacity of the resource') }}</label>
                      <input id="count" class="form-control" name="count" type="number" data-android-inputmode="decimal" v-model="resource.count" :disabled="!!resource.seatmap_id" />
                    </div>
                    <p v-if="resource.seatmap_id">{{ _('This resource has a seat map, so its capacity is determined automatically by the number of seats drawn in the seat map editor.') }}</p>
                    <p v-else>{{ _('The capacity of a single resource is defined by entering the value in the field "Maximum capacity of the resource".') }}
                    {{ _('In practice this means that a single room may have several beds or a single canoe may have more than one seat.') }}</p>
                    <div class="form-group">
                      <label for="extraCount">{{ _('Extra capacity of the resource') }}</label>
                      <input id="extraCount" class="form-control" name="extraCount" type="number" data-android-inputmode="decimal" v-model="resource.extracount" />
                    </div>
                    <p>{{ _('If you would like to have extra capacity available via the POS/management interface, enter the number of extra seats in this field.') }}</p>
                    <div class="checkbox">
                      <label>
                      <input type="checkbox" name="enable_shared_reservation" :disabled="resource.quantity > 1" v-model="resource.enable_shared_reservation" true-value="1" false-value="0" />
                      {{ _('Allow separate reservations at the same time') }}
                      </label>
                    </div>
                    <p>{{ _('The setting "Allow separate reservations at the same time" is used to enable/disable simultaneous reservations of the resource during the same time.') }}
                    {{ _('For example, if you rent bicycles, you can allow anyone to reserve them even one at a time.') }}
                    {{ _('On the other hand, if you offer something like private tours, you\'ll want to disable this setting.') }}</p>
                    <template v-if="parseInt(resource.enable_shared_reservation) === 1">
                      <div class="checkbox">
                        <label>
                        <input type="checkbox" name="shared_reservation_scope" v-model="resource.shared_reservation_scope" true-value="departure" false-value="" />
                        {{ _('Share only within the same departure') }}
                        </label>
                      </div>
                      <p>{{ _('When this is on, the resource accepts several reservations for the same product and start time, but it is unavailable for other products at overlapping times.') }}
                      {{ _('Use this for guides who can lead only one activity at a time.') }}</p>
                    </template>
                    <div class="checkbox">
                      <label>
                      <input type="checkbox" name="hidden" v-model="resource.hidden" true-value="1" false-value="0" />
                      {{ _('Hide from timeline view') }}
                      </label>
                    </div>
                    <div class="checkbox">
                      <label>
                      <input type="checkbox" name="grouped" v-model="resource.grouped" true-value="1" false-value="0" />
                      {{ _('Group reservations in timeline view') }}
                      </label>
                    </div>
                    <div class="checkbox" v-if="settings.calendarauthcode && settings.calendarrefreshtoken">
                      <label>
                        <input type="checkbox" name="enable_calendar" v-model="resource.enable_calendar" true-value="1" false-value="0" />
                        {{ _('Synchronize reservations to Google Calendar') }}
                      </label>
                    </div>
                    <div class="checkbox">
                      <label>
                      <input type="checkbox" name="skiptickets" v-model="resource.skiptickets" true-value="1" false-value="0" />
                      {{ _('Do not create tickets for this resource') }}
                      </label>
                    </div>
                    <div class="checkbox" v-if="isFeatureEnabled('delivery2.0')">
                      <label>
                        <input type="checkbox" name="display_name_in_workflow" v-model="resource.display_name_in_workflow" true-value="1" false-value="0" />
                        {{ _('Display name in workflow') }}
                      </label>
                    </div>
                  </div>
                </template>
                <hr />
                <div class="form-group languageversioned">
                  <label for="description">{{ _('Description') }}</label>
                  <textarea id="description" class="form-control" name="description" rows="8" wrap="virtual" v-model="resource.description"></textarea>
                </div>
              </div>
              <div class="col-lg-6 col-sm-4 rightcol">
                <p>{{ _('Resources represent the physical things associated with a product, for example a bike or a hotel room. A product must be connected to at least one resource. You can also create product packages containing several resources.') }}</p>
                <p v-if="resource.displayedtype === 'buyable'">{{ _('A single resource may also be attached to several products: the sale of these products will be constrained by the resource\'s available stock balance. If you mix and match multiple resources with multiple products, Johku will only allow the product to be sold when all resources attached to the product are in stock.') }}</p>
                <p v-else-if="resource.displayedtype === 'openinghours'">{{ _('Opening hours resources are special: they can be used to easily adjust the availability of multiple products.') }} {{ _('Due to their special nature they don\'t have a capacity or associated reservations.') }}</p>
                <p v-else>{{ _('A single resource may also be attached to several products: reservations to these products will be constrained by the resource\'s reservation calendar. If you mix and match multiple resources with multiple products, Johku will only allow reservations when all resources related to the product are available at the same time.') }}</p>
                <hr/>
                <div id="otherReservations" v-if="resource.displayedtype !== 'buyable'">
                  <div v-if="reservations.length">
                    <h3>{{ _('All reservations for the resource') }}</h3>
                    <div class="list-group">
                      <a v-for="reservation in reservations" class="reservation_date_item list-group-item">
                      <h4 v-if="parseInt(reservation.status)" class="reservation_date_client reservation_confirmed list-group-item-heading">{{ reservation.client_name ? reservation.client_name : _('Johku App') }}</h4>
                      <h4 v-else class="reservation_date_client reservation_tentative list-group-item-heading">{{ _('Tentative') }}</h4>
                      <p class="list-group-item-text reservation_date_str">
                        {{ DateTime.fromSeconds(Number(reservation.start_ts), { zone: timezone }).toFormat(dateFormat) }}
                        {{ _('at') }}
                        {{ DateTime.fromSeconds(Number(reservation.start_ts), { zone: timezone }).toFormat(timeFormat) }}
                        -
                        <span v-if="DateTime.fromSeconds(Number(reservation.start_ts), { zone: timezone }).toFormat('YYYY/MM/DD') !== DateTime.fromSeconds(Number(reservation.end_ts), { zone: timezone }).toFormat('YYYY/MM/DD')">{{ DateTime.fromSeconds(Number(reservation.end_ts), { zone: timezone }).toFormat(dateFormat) }} {{ _('at') }} </span>
                        {{ DateTime.fromSeconds(Number(reservation.end_ts), { zone: timezone }).toFormat(timeFormat) }}
                      </p>
                      </a>
                    </div>
                  </div>
                  <div v-else>
                    {{ _('There are no reservations for the resource.') }}
                  </div>
                </div>
                <hr v-if="resource.displayedtype !== 'buyable'" />
                <div id="resourcePools">
                  <div class="list-group">
                    <h3 v-if="resource.sharedResourcepool && resource.sharedResourcepool.length">{{ _('Resource pools') }}</h3>
                    <p v-else>{{ _('The resource is not a member of any resource pools.') }}</p>
                    <a v-for="pool in resource.sharedResourcepool" class="list-group-item poolLink" href="javascript:;" :data-id="pool.id">{{ pool.name }}</a>
                  </div>
                </div>
                <hr/>
                <div id="resourceProducts">
                  <div class="list-group" v-if="resource.sharedProduct && resource.sharedProduct.length">
                    <h3>{{ _('Products') }}</h3>
                    <a v-for="prod in resource.sharedProduct.filter(p => !parseInt(p.deleted))" class="list-group-item prodLink" href="javascript:;">{{ prod.name }}</a>
                    <div>
                      <br>
                      <p>{{ _('If you wish to remove this resource from a product that has multiple sources of availabilility, edit the availability of the product in question and use the \'Remove from this product\' link next to this resource.') }}</p>
                    </div>
                  </div>
                  <p v-else>{{ _('The resource is not directly attached to any products.') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('availability')" class="tab-pane active" id="availability" v-if="isTabActive('availability')" key="availability">
            <div class="row">
              <div class="col-xs-12">
                <div class="form-group">
                  <h3>{{ _('RESERVABLE_TIMES_START') }}</h3>
                </div>
                <table class="table">
                  <tbody class="timerows" id="ranges">
                    <tr v-for="range in ranges" :class="'range' + (makeRangeDesc(range).match(/fa-ban/) ? ' bg-primary' : '') + (range.ext_id ? ' external': '')">
                      <td v-html="makeRangeDesc(range)" v-on:click="editRange(range)"></td>
                    </tr>
                  </tbody>
                </table>
                <button class="addtimerow btn btn-success" type="button" v-on:click="addRange"><i class="fa fa-fw fa-plus"></i> {{ _('Add range') }}</button>
              </div>
            </div>
            <hr v-if="settings.calendarauthcode" />
            <div class="row" v-if="settings.calendarauthcode" >
              <div class="col-xs-12">
                <div class="form-group">
                  <h3>{{ _('Google Calendar') }}</h3>
                </div>
                <div class="form-group">
                  <label id="googleCalendarDesc" v-if="resource.google_calendar_id">
                  {{ _('Limit availability based on events in calendar:') }} {{ resource.google_calendar_title }} ({{ resource.google_calendar_id }})
                  </label>
                  <label id="googleCalendarDesc" v-else>
                  {{ _('Select a calendar to limit availability based on the events in it.') }}
                  </label>
                </div>
                <button v-if="!resource.google_calendar_id" class="selectcalendar btn btn-success" id="selectCalendarButton" type="button" v-on:click="selectCalendar"><i class="fa fa-fw fa-calendar-o"></i> {{ _('Select calendar') }}</button>
                <button v-else class="detachcalendar btn btn-danger" id="detachCalendarButton" type="button" v-on:click="detachCalendar"><i class="fa fa-fw fa-calendar-times-o"></i> {{ _('Detach calendar') }}</button>
              </div>
            </div>
            <template v-if="isLockingVisible">
              <hr />
              <div class="row">
                <div class="col-xs-12">
                  <div class="form-group">
                    <h3>{{ _('Locking') }}</h3>
                  </div>
                  <div class="form-group">
                    <label v-if="resource.igloohome_lockcode">
                    {{ _('Linked to Igloohome lock') }}: {{ resource.igloohome_lockname }}
                    </label>
                    <label v-else-if="resource.ratna_lockcode">
                    {{ _('Linked to Ratna lock') }}: {{ resource.ratna_lockname }} ({{ resource.ratna_lockcode }})
                    </label>
                    <label v-else>
                    {{ _('Select a lock to link this resource to.') }}
                    </label>
                  </div>
                  <div class="form-group row" id="iglooOffsetContainer" v-if="resource.igloohome_lockcode">
                    <div class="col-xs-6">
                      <label for="igloohome_startoffset">{{ _('Number of hours to enable PIN code before reservation start') }}</label>
                      <input type="number" data-android-inputmode="decimal" class="form-control" name="igloohome_startoffset" v-model.number="resource.igloohome_startoffset" />
                    </div>
                    <div class="col-xs-6">
                      <label for="igloohome_endoffset">{{ _('Number of hours to extend PIN code validity after reservation end') }}</label>
                      <input type="number" data-android-inputmode="decimal" class="form-control" name="igloohome_endoffset" v-model.number="resource.igloohome_endoffset" />
                    </div>
                  </div>
                  <div class="form-group row" id="ratnaOffsetContainer" v-else-if="resource.ratna_lockcode">
                    <div class="col-xs-6">
                      <label for="ratna_startoffset">{{ _('Number of hours to enable PIN code before reservation start') }}</label>
                      <input type="number" data-android-inputmode="decimal" class="form-control" name="ratna_startoffset" v-model.number="resource.ratna_startoffset" />
                    </div>
                    <div class="col-xs-6">
                      <label for="ratna_endoffset">{{ _('Number of hours to extend PIN code validity after reservation end') }}</label>
                      <input type="number" data-android-inputmode="decimal" class="form-control" name="ratna_endoffset" v-model.number="resource.ratna_endoffset" />
                    </div>
                  </div>
                  <template v-if="(settings.igloohome_apikey || resource.igloohome_lockcode) && !resource.ratna_lockcode">
                    <button class="selectigloo btn btn-success" id="selectIglooButton" type="button" v-if="resource.igloohome_apikey && !resource.igloohome_lockcode" v-on:click="selectIglooLock"><i class="fa fa-fw fa-lock"></i> {{ _('Select Igloohome lock') }}</button>
                    <button class="detachigloo btn btn-danger" id="detachIglooButton" type="button" v-else v-on:click="detachIglooLock"><i class="fa fa-fw fa-lock"></i> {{ _('Detach Igloohome lock') }}</button>
                  </template>
                  <template v-if="(settings.ratna_username || resource.ratna_lockcode) && !resource.igloohome_lockcode">
                    <button class="selectratna btn btn-success" id="selectRatnaButton" type="button" v-if="resource.ratna_username && !resource.ratna_lockcode" v-on:click="selectRatnaApartment"><i class="fa fa-fw fa-lock"></i> {{ _('Select Ratna apartment') }}</button>
                    <button class="detachratna btn btn-danger" id="detachRatnaButton" type="button" v-else v-on:click="detachRatnaApartment"><i class="fa fa-fw fa-lock"></i> {{ _('Detach Ratna apartment') }}</button>
                  </template>
                </div>
              </div>
            </template>
          </div>
          <div v-bind="tabPanel('smartdevices')" class="tab-pane active" id="smartdevices" v-if="isTabActive('smartdevices')" key="smartdevices">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Smart Devices') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <table class="table table-striped" v-if="resource.ownResource_smartdevice && resource.ownResource_smartdevice.length">
                  <thead>
                    <tr>
                      <th>{{ _('Name') }}</th>
                      <th>{{ _('Description') }}</th>
                      <th colspan="2">{{ _('Type') }}</th>
                    </tr>
                  </thead>
                  <tbody class="messagerows" id="smartdevices">
                    <tr v-for="link in resource.ownResource_smartdevice">
                      <td class="smartdevice" v-on:click="editSmartDevice(link)">{{ makeSmartDeviceTitle(link) }}</td>
                      <td class="smartdevice" v-on:click="editSmartDevice(link)">{{ makeSmartDeviceDesc(link) }}</td>
                      <td v-on:click="editSmartDevice(link)"><i :class="'fa fa-fw fa-' + makeSmartDeviceIcon(link)"></i> {{ makeSmartDeviceType(link) }} </td>
                      <td style="width: 20px"><a class="deleteSmartDevice pull-right" href="javascript:;" v-on:click="deleteSmartDevice(link)"><i class="fa fa-fw fa-close"></i></a></td>
                    </tr>
                  </tbody>
                </table>
                <div v-else>
                  <p>{{ _('No smart devices have been connected.') }}</p>
                </div>
                <button type="button" class="addmessage btn btn-success" v-on:click="addSmartDevice()"><i class="fa fa-fw fa-plus"></i> {{ _('Add device') }}</button>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('seatmap')" class="tab-pane active" id="seatmap" v-if="isTabActive('seatmap')" key="seatmap">
            <div class="row">
              <div class="col-xs-12">
                <h3>{{ _('Seat Map') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <p><i>{{ _('The seat map feature is in beta and only available for event resources.') }}</i></p>
                <div v-if="resource.seatmap_id">
                  <p><strong><i class="fa fa-fw fa-check"></i> {{ _('This resource has a seat map defined.') }}</strong></p>
                  <div class="button-col">
                    <button type="button" class="btn btn-primary" v-on:click="openSeatMap"><i class="fa fa-fw fa-sitemap"></i> {{ _('Show seat map') }}</button>
                    <button type="button" class="btn btn-info" v-on:click="updateSeatData" v-if="isUpdateSeatDataButtonVisible" :disabled="updatingSeatData"><div :class="updateSeatDataClass"></div> {{ _('Regenerate seat info') }}</button>
                    <button type="button" class="btn btn-info" v-on:click="openSeatmapEditor" v-if="isSeatmapEditorButtonVisible"><i class="fa fa-fw fa-pencil"></i> {{ _('Edit seat map') }}</button>
                  </div>
                </div>
                <template v-else>
                  <p><strong>{{ _('This resource does not have a seat map.') }}</strong></p>
                  <button type="button" class="btn btn-primary" v-on:click="openSeatmapEditor" v-if="isSeatmapEditorButtonVisible"><i class="fa fa-fw fa-plus"></i> {{ _('Create seat map') }}</button>
                </template>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('messages')" class="tab-pane active" id="messages" v-if="isTabActive('messages')" key="messages">
            <div class="row">
              <div class="col-xs-12 languageversioned">
                <h3>{{ _('Scheduled messages') }}</h3>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-6 leftcol">
                <table class="table table-striped" v-if="notifications.length">
                  <thead>
                    <tr>
                      <th>{{ _('Name') }}</th>
                      <th colspan="2">{{ _('Type') }}</th>
                    </tr>
                  </thead>
                  <tbody class="messagerows" id="notifications">
                    <tr v-for="notification in notifications">
                      <td class="notification" v-on:click="editNotification(notification, true)">{{ makeNotificationDesc(notification) }}</td>
                      <td v-on:click="editNotification(notification, true)">{{ makeNotificationType(notification) }} </td>
                      <td style="width: 20px"><a class="deleteNotification pull-right" href="javascript:;" v-on:click="deleteNotification(notification)"><i class="fa fa-fw fa-close"></i></a></td>
                    </tr>
                  </tbody>
                </table>
                <div v-else>
                  <p>{{ _('No notifications have been defined.') }}</p>
                </div>
                <button type="button" class="addmessage btn btn-success" v-on:click="addNotification(true)"><i class="fa fa-fw fa-plus"></i> {{ _('Add message') }}</button>
              </div>
              <div class="col-lg-6 rightcol">
                <div style="hidden-lg">&nbsp;</div>
                <p>{{ _('Create customer messages that can be sent automatically in response to various events related to orders and reservations. You can use these messages to, for example, inform the customer of delivery or ask for feedback after the reservation has ended.') }} <a id="gotoSupportSite" href="http://support.johku.com" target="_blank">{{ _('Learn more on the support site.') }}</a></p>
              </div>
            </div>
            <div class="row">
              <div class="col-xs-12">
                <div class="accessinfo-section">
                  <h3>{{ _('Access & Usage Instructions') }}</h3>
                  <div class="form-group languageversioned">
                    <label for="accessinfo_title">{{ _('Title') }}</label>
                    <input type="text" class="form-control" id="accessinfo_title" name="accessinfo_title" v-model="resource.accessinfo_title" />
                  </div>
                  <div class="form-group languageversioned">
                    <label for="accessinfo">{{ _('Text') }}</label>
                    <editor v-model="resource.accessinfo" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove(_('Are you sure you want to delete the resource? The resource will be completely removed from the system and the deletion can\'t be undone.'))" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete resource') }}</span></button>
        <button class="btn btn-info" v-on:click="translateDialog" type="button" v-if="isTranslateButtonVisible" :disabled="!isTranslateButtonEnabled"><i class="fa fa-fw fa-language"></i><span class="hidden-xs"> {{ _('Translate') }}</span></button>
        <div class="button-col">
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
        </div>
      </div>
      <seat-map ref="seatMap" :column="column" :show="fakeShow" :showButtons="false" :seatMap="seatMap" v-if="seatMap && showSeatMap" v-on:closeModal="closeSeatMap"></seat-map>
      <SeatmapEditor
        v-if="showSeatmapEditor"
        :initial-data="seatmapEditorInitialData"
        :saving="savingSeatmapEditor"
        v-on:save="saveSeatmapEditor"
        v-on:close="closeSeatmapEditor"
      ></SeatmapEditor>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import notifications from '~/lib/notifications'
import rangehelpers from '~/lib/rangehelpers'
import resourcetypes from '~/lib/resourcetypes'
import permissions from '~/lib/permissions'
import seatmap from '~/lib/seatmap'
import settingshelpers from '~/lib/settingshelpers'
import smartdevices from '~/lib/smartdevices'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import { PricetierSelector, DatetimePicker, GoogleCalendarSelector, IglooLockSelector, TranslationSelector, SeatMap, SeatmapEditor, Editor } from '#components'

export default {
  data () {
    return {
      resource: {},
      deleted: false,
      ranges: [],
      selectedResource: null,
      basketItemCount: 0,
      originalCapacity: false,
      showSeatMap: false,
      seatMap: null,
      showSeatmapEditor: false,
      seatmapEditorInitialData: null,
      savingSeatmapEditor: false,
      notifications: [],
      reservations: [],
      smartdevices: [],
      DateTime: DateTime,
      updatingSeatData: false
    }
  },
  mixins: [changewatcher, components, notifications, permissions, rangehelpers, resourcetypes, seatmap, settingshelpers, smartdevices],
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('General') },
        { id: 'availability', label: this._('Availability') },
        { id: 'smartdevices', label: this._('Smart Devices'), hidden: !this.isFeatureEnabled('smartdevices') },
        { id: 'seatmap', label: this._('Seat map'), hidden: !this.isSeatmapTabVisible },
        { id: 'messages', label: this._('Messages') }
      ])
    },
    isUnitLocked () {
      return this.resource && !!parseInt(this.resource.unit_locked)
    },
    isUpdateSeatDataButtonVisible () {
      return this.userData && parseInt(this.userData.userlevel, 10) === 10
    },
    isSeatmapEditorButtonVisible () {
      return this.canAccessSettings && this.isFeatureEnabled('seatmapeditor')
    },
    isSeatmapTabVisible () {
      return !!(this.resource && this.resource.id && (this.resource.seatmap_id || this.resource.displayedtype === 'theater'))
    },
    updateSeatDataClass () {
      return 'fa fa-fw fa-refresh' + (this.updatingSeatData ? ' fa-spin' : '')
    },
    isTranslateButtonVisible () {
      return this.isFeatureEnabled('translation')
    },
    isTranslateButtonEnabled() {
      return this.locale !== this.version
    },
    isLockingVisible() {
      /*
      if (this.resource && this.resource.displayedtype === 'openinghours') {
        return false
      }
      */
      // Changed this so that we don't show this thing to new locks, they should use Smart Devices instead
      if (this.isFeatureEnabled('smartdevices')) {
        return this.resource && (this.resource.igloohome_lockcode || this.resource.ratna_lockcode)
      }
      return this.settings.igloohome_apikey || this.settings.ratna_username
    },
    isNewResource () {
      return !parseInt(this.resource.id)
    },
    fakeShow() {
      return {
        resource: this.resource
      }
    },
    limitedReservableResourceTypes() {
      let $this = this
      if(!this.resource && this.resource.displayedtype) {
        return this.reservableResourceTypes.filter(type => type.value === 'generic' || type.value === 'theater')
      }
      return this.reservableResourceTypes.filter(type => 
        type.value === 'generic' || type.value === 'theater' || type.value === $this.resource.displayedtype
      )
    },
    ratnaApartments: {
      get () {
        return this.settings && this.settings.ratna_apartments ? JSON.parse(this.settings.ratna_apartments) : []
      },
      set () {}
    }
  },
  methods: {
    isConditionalTab(tab) {
      if (tab === 'availability' || tab === 'seatmap' || tab === 'smartdevices') {
        if (this.resource && this.resource.id && this.resource.displayedtype === 'buyable') {
          return true
        }
      } 
      return false
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'displayedtype',
        'custom_id',
        'mincount',
        'count',
        'extracount',
        'unit',
        'description',
        'accessinfo_title',
        'accessinfo',
        'google_calendar_id',
        'google_calendar_mode',
        'google_calendar_title',
        'igloohome_startoffset',
        'igloohome_endoffset',
        'igloohome_lockcode',
        'igloohome_lockname',
        'ratna_startoffset',
        'ratna_endoffset',
        'ratna_lockcode',
        'ratna_lockname',
        'enable_shared_reservation',
        'shared_reservation_scope',
        'hidden',
        'grouped',
        'enable_calendar',
        'skiptickets',
        'seatmap_id',
        'seatmap_data',
        'display_name_in_workflow'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.resource[e], a), {})
      if (data.displayedtype === 'buyable') {
        data.count = this.resource.capacity
        data.originalcount = this.originalCapacity
      }
      data.source = 'editor'
      data.sharedNotification = this.notifications
      data.ownResource_smartdevice = this.resource.ownResource_smartdevice
      data.ownRange = this.ranges
      data.version = this.version
      return data
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/resources/' + this.resource.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deleting the resource failed!'))
      }
      this.showNotification(this._('Resource deleted!'))
      this.$emit('deleteResource', this.resource)
      this.close()
    },
    async save () {
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/resources/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/resources.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadResource(response.data.id)
      }
      this.saving = false
      this.saved = true
      this.storeState()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    async reloadOnVersionChange () {
      if (!this.resource || !this.resource.id) {
        return
      }
      await this.loadResource(this.resource.id)
    },
    loadResource (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/resources/' + id + '.json?basketitemcount=true&capacity=true&products=true&resourcepools=true&smartdevices=true&unitcheck=true&apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.resource = response.data
        this.resource.ownResource_smartdevice.forEach(link => {
          link.data = JSON.parse(link.data)
          if (!link.data) {
            link.data = {}
          }
        })
        this.ranges = []
        this.reservations = []
        const loaders = []
        if (this.resource.displayedtype !== 'buyable') {
          loaders.push(this.loadRanges())
          loaders.push(this.loadReservations())
        }
        loaders.push(this.loadNotifications('resource', this.resource.id))
        await Promise.allSettled(loaders)
        this.originalCapacity = this.resource.capacity
        this.storeState()
        this.loading = false
        resolve(this.resource)
      })
    },
    loadReservations() {
      return new Promise(async (resolve, reject) => {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/resources/' + this.resource.id + '/reservations.json?apikey=' + this.apiKey)
        } catch(e) {
          this.reservations = []
          reject(e)
          return
        }
        this.reservations = response.data
        resolve(response.data)
      })
    },
    async openSeatMap() {
      if (!this.seatMap) {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/seatmaps/' + this.resource.seatmap_id + '.json?apikey=' + this.apiKey)
        } catch(e) {
          this.showError(e)
          return
        }
        this.seatMap = response.data
      }
      this.showSeatMap = true
    },
    async updateSeatData() {
      this.updatingSeatData = true
      try {
        await axios.patch(this.baseDomain + this.shopId + '/seatmaps/' + this.resource.seatmap_id + '.json?apikey=' + this.apiKey, { id: this.resource.seatmap_id })
        this.seatMap = null
        await this.loadResource(this.resource.id)
        this.showNotification(this._('Changes saved successfully!'))
      } catch(e) {
        this.showError(e)
      }
      this.updatingSeatData = false
    },
    async openSeatmapEditor() {
      this.seatmapEditorInitialData = null
      if (this.resource.seatmap_id) {
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/seatmaps/' + this.resource.seatmap_id + '.json?apikey=' + this.apiKey)
        } catch(e) {
          return this.showError(e)
        }
        try {
          this.seatmapEditorInitialData = response.data && response.data.data ? JSON.parse(response.data.data) : null
        } catch(e) {
          this.seatmapEditorInitialData = null
        }
      }
      this.showSeatmapEditor = true
    },
    closeSeatmapEditor() {
      this.showSeatmapEditor = false
      this.seatmapEditorInitialData = null
    },
    async saveSeatmapEditor(exportedData) {
      this.savingSeatmapEditor = true
      const data = this.getSaveData()
      data.seatmap_data = JSON.stringify(exportedData)
      let response
      try {
        response = await axios.put(this.baseDomain + this.shopId + '/resources/' + data.id + '.json?apikey=' + this.apiKey, data)
      } catch(e) {
        this.savingSeatmapEditor = false
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadResource(response.data.id)
      }
      this.savingSeatmapEditor = false
      this.showSeatmapEditor = false
      this.seatmapEditorInitialData = null
      this.showNotification(this._('Changes saved successfully!'))
    },
    selectCalendar() {
      this.showModal('googlecalendarselector', GoogleCalendarSelector, {
        props: {
          settings: this.settings,
          locale: this.shortLocale
        },
        events: {
          selectCalendar: this.calendarSelected
        },
        handlers: {},
        width: 800
      })
    },
    detachCalendar() {
      this.resource.google_calendar_id = null
      this.resource.google_calendar_title = null
    },
    calendarSelected (obj) {
      let calendar = obj.calendar
      let mode = parseInt(obj.mode)
      this.resource.google_calendar_id = calendar.id
      this.resource.google_calendar_title = calendar.summary
      this.resource.google_calendar_mode = mode
    },
    selectIglooLock() {
      this.showModal('igloolockselector', IglooLockSelector, {
        props: {
          settings: this.settings,
          locale: this.shortLocale
        },
        events: {
          selectLock: this.iglooLockSelected
        },
        handlers: {},
        width: 800
      })
    },
    detachIglooLock() {
      this.resource.igloohome_lockcode = null
      this.resource.igloohome_lockname = null
    },
    iglooLockSelected (lock) {
      this.resource.igloohome_lockcode = lock._id
      this.resource.igloohome_lockname = lock.name
    },
    selectRatnaApartment() {
      this.showDialog({
        title: this._('Select apartment'),
        width: 800,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Select'),
            class: 'success',
            icon: 'check',
            requiredFields: ['id'],
            handler: this.ratnaApartmentSelected
          }
        ],
        fields: [
          {
            title: this._('Apartment'),
            type: 'select',
            name: 'id',
            options: this.ratnaApartments.map(a => { return { value: a.id, name: a.name } })
          },
        ]
      })
    },
    detachRatnaApartment() {
      this.resource.ratna_lockcode = null
      this.resource.ratna_lockname = null
    },
    ratnaApartmentSelected (data) {
      if (!data.id) {
        return
      }
      const apartment = this.ratnaApartments.find(a => a.id === data.id)
      if (apartment) {
        this.resource.ratna_lockcode = apartment.id
        this.resource.ratna_lockname = apartment.name
      }
    },
    translateDialog () {
      const $this = this
      this.showModal('translationselector', TranslationSelector, {
        props: {
          title: this._('Select texts to be translated'),
          settings: this.settings,
          baseDomain: this.baseDomain,
          shopId: this.shopId,
          apiKey: this.apiKey,
          component: {
            initData: {
              modal: true
            }
          },
          parent: this.resource,
          parentType: 'resources',
          className: 'modal-content'
        },
        events: {
          result (data) {
            for (let i = 0; i < data.length; i++) {
              const d = data[i]
              if (d.field.includes('.')) {
                let [type, field] = d.field.split('.')
                if (type.match(/^notification\-/)) {
                  let [, nId] = type.split('-')
                  for (let i = 0; i < $this.notifications.length; i++) {
                    if ($this.notifications[i].id == nId) {
                      this.notifications[i][field] = d.text
                    }
                  }
                }
              } else {
                this.resource[d.field] = d.text
              }
            }
          }
        },
        handlers: {},
        width: 1000
      })
    }
  },
  components: {
    PricetierSelector,
    DatetimePicker,
    SeatMap,
    SeatmapEditor,
    Editor
  },
  created () {
    if (this.component && this.component.initData && !this.loading) {
      this.loadResource(this.component.initData.resourceId)
    }
  },
  async updated () {
    if (this.component && this.component.initData && !this.loading) {
      if (!this.deleted && this.resource && this.resource.id && parseInt(this.resource.id, 10) !== parseInt(this.component.initData.resourceId, 10)) {
        try {
          await this.loadResource(this.component.initData.resourceId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['resourceUpdate'] = async (message) => {
        if (message && message.data && this.resource && parseInt(message.data.id, 10) === parseInt(this.resource.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadResource(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.removeitem {
  cursor: pointer;
}
.well-info {
  background-color: #d9edf7;
}
.well-warning {
  background-color: #fcf8e3;
}
.well {
  margin-top: 20px;
}
.accessinfo-section {
  margin-top: 30px;
}
.well h3 {
  margin-top: 11px;
}
</style>
